import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastError } from '~/utils/useToastError';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { FullMenuTable } from './FullMenuTable';
import { GrayBanner } from '~/ui/GrayBanner/GrayBanner';
import { Button, InputBar, Pagination, Spinner } from '~/ui';
import '~/assets/css/select-table.css';
import styles from './FullMenuBoard.module.scss';
import { useTranslation } from 'react-i18next';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { translateFoodTypes, translateFoodTypeItem } from '~/utils/translateOptions';

export interface ISelectedWares {
  id: number;
  price: string;
  max_amount: string;
  weight: string;
  measure: string;
  position: string;
}

interface SelectedPage {
  selected: number;
}

interface FoodType {
  id: number;
  name: string;
}

interface FullMenuBoardProps {
  foodId: number;
  foodPointType: number;
  selectedDate: string;
  onClose: () => void;
}

export const FullMenuBoard = ({
  foodId,
  selectedDate,
  onClose,
  foodPointType,
}: FullMenuBoardProps): JSX.Element => {
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [page, setPage] = useState<number>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWares, setSelectedWares] = useState<ISelectedWares[]>([]);
  const [hasAllPrice, setHasAllPrice] = useState(false);
  const [foodType, setFoodType] = useState<FoodType>({
    id: 0,
    name: '',
  });
  const toastError = useToastError();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { t } = useTranslation();

  const params = {
    sort: sort.direction,
    perPage: 5,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
    ...(foodPointType === 1 ? { date: selectedDate } : {}),
    food_id: foodId,
  };

  const client = useQueryClient();

  const {
    data: menu,
    isError,
    error,
  } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/ifcm-wares-all', {
        params,
      });
      return response.data;
    },

    queryKey: ['ifcm-wares-all', foodId, params],
    keepPreviousData: true,
  });

  const { data: foodTypes } = useQuery({
    queryFn: async () => {
      const response = await instance.get(`food-type?food_id=${foodId}`);
      const firstItem = response?.data?.data[0];
      const translatedFirst = translateFoodTypeItem(firstItem, t);
      setFoodType({
        id: firstItem.id,
        name: translatedFirst?.display_name ?? firstItem.name,
      });
      return response.data;
    },
    queryKey: ['food-types'],
    onError: error => {
      toastError(error);
    },
  });

  // Переведённые типы блюд для дропдауна
  const translatedFoodTypes = translateFoodTypes(foodTypes?.data, t);

  const handleClose = () => {
    setSelectedWares([]);
    const firstItem = foodTypes?.data[0];
    const translatedFirst = firstItem ? translateFoodTypeItem(firstItem, t) : null;
    setFoodType({
      id: firstItem?.id,
      name: translatedFirst?.display_name ?? firstItem?.name,
    });
    onClose();
  };

  const { mutate: createCurrentMenu } = useMutation(
    async () => {
      const response = await instance.post('admin/food-wares', {
        wares_id: selectedWares,
        food_id: foodId,
        ...(foodPointType === 1 ? { date: selectedDate } : {}),
        visibility: 1,
        food_types_ids: [foodType.id],
      });
      return response.data;
    },
    {
      onSuccess() {
        client.invalidateQueries(['food-wares', foodId]);
        handleClose();
      },
      onError(error) {
        toastError(error);
      },
    }
  );

  let content: React.ReactNode;

  if (menu?.data && foodId) {
    content = (
      <FullMenuTable
        menu={menu?.data}
        sort={sort}
        setSort={setSort}
        selectedWares={selectedWares}
        setSelectedWares={setSelectedWares}
      />
    );
  } else if (isError) {
    content = <ErrorMessage error={error} />;
  } else {
    content = <Spinner />;
  }

  useEffect(() => {
    const hasEmptyValue = selectedWares.some(
      item =>
        item?.price?.trim() === '' ||
        item?.price === null ||
        item?.max_amount?.trim() === '' ||
        item?.max_amount === null ||
        item?.weight?.trim() === '' ||
        item?.weight === null ||
        item?.measure?.trim() === '' ||
        item?.weight === null ||
        item?.position?.trim() === '' ||
        item?.position === null,
    );
    setHasAllPrice(hasEmptyValue);
  }, [selectedWares]);

  return (
    <Board classNames={styles.board}>
      <div className={styles.boardTop}>
        <h3 className={styles.heading}>{t('add_dish')}</h3>
        <Button
          onClick={handleClose}
          text={t('close')}
          color="red"
          disabled={false}
          className={styles.closeBtn}
        />
      </div>

      <GrayBanner
        text={t('selected_dishes_displayed_one_service')}
        className={styles.fullMenuBanner}
      />

      <div className={styles.options}>
        <InputBar
          onChange={e => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          value={searchTerm}
          placeholder={t('search_by_name')}
          classNameInput={styles.input}
          label={`${t('search')}:`}
          star={false}
          classNameInputWrapper={styles.inputWrapper}
        />

        <div className={styles.foodTypeSelect}>
          {translatedFoodTypes?.length > 0 && (
            <CustomSelect
              label=""
              placeholder={t('choose')}
              options={translatedFoodTypes}
              value={foodType}
              onChange={value => {
                if (!Array.isArray(value)) {
                  setFoodType({
                    id: Number(value.id),
                    name: (value as any).display_name ?? (value as any).name,
                  });
                }
              }}
            />
          )}
        </div>
      </div>
      {content}
      <div className={styles.pagination}>
        <TableAmountPage
          firstRow={menu?.meta?.from}
          lastRow={menu?.meta?.to}
          total={menu?.meta?.total}
        />
        <Pagination
          pageCount={menu?.meta?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
      <div className={styles.boardBottom}>
        <Button
          onClick={() => createCurrentMenu()}
          text={t('save_n_exit')}
          className={styles.saveBtn}
          disabled={!(selectedWares.length && foodType) || hasAllPrice}
        />
      </div>
    </Board>
  );
};
